/**
 * Carte des metriques de performance
 * Design System v2 (Volvo-inspired)
 */

import {
  Activity,
  Server,
  Database,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
} from 'lucide-react';
import { usePerformanceMetrics } from '../hooks/useAlerts';
import type { DatabaseStatus } from '../types/alerts.types';
import { DATABASE_STATUS_COLORS } from '../types/alerts.types';

export function PerformanceMetricsCard() {
  const { data: metrics, isLoading, refetch, isRefetching, dataUpdatedAt } = usePerformanceMetrics();

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days}j ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatBytes = (bytes: number) => {
    const mb = bytes / 1024 / 1024;
    if (mb > 1024) return `${(mb / 1024).toFixed(1)} GB`;
    return `${mb.toFixed(1)} MB`;
  };

  const getStatusIcon = (status: DatabaseStatus) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'degraded':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'down':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-gray-500">
        Impossible de charger les metriques
      </div>
    );
  }

  const memoryUsage = (metrics.server.memory.heapUsed / metrics.server.memory.heapTotal) * 100;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-100 rounded-lg">
            <Activity className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Performance Systeme</h2>
            <p className="text-sm text-gray-500">
              Mis a jour: {new Date(dataUpdatedAt).toLocaleTimeString('fr-FR')}
            </p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isRefetching}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 ${isRefetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Main Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Requests */}
        <div className="p-4 bg-gray-50 rounded-xl">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <Activity className="w-4 h-4" />
            <span className="text-sm">Requetes/h</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {metrics.requestCount.toLocaleString()}
          </div>
        </div>

        {/* Error Rate */}
        <div className="p-4 bg-gray-50 rounded-xl">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">Taux Erreur</span>
          </div>
          <div
            className={`text-2xl font-bold ${
              metrics.errorRate > 5
                ? 'text-red-600'
                : metrics.errorRate > 2
                  ? 'text-orange-600'
                  : 'text-green-600'
            }`}
          >
            {metrics.errorRate.toFixed(1)}%
          </div>
        </div>

        {/* Response Time */}
        <div className="p-4 bg-gray-50 rounded-xl">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Temps Moyen</span>
          </div>
          <div
            className={`text-2xl font-bold ${
              metrics.averageResponseTime > 1000
                ? 'text-red-600'
                : metrics.averageResponseTime > 500
                  ? 'text-orange-600'
                  : 'text-green-600'
            }`}
          >
            {metrics.averageResponseTime}ms
          </div>
        </div>

        {/* Errors */}
        <div className="p-4 bg-gray-50 rounded-xl">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">Erreurs/h</span>
          </div>
          <div
            className={`text-2xl font-bold ${
              metrics.errorCount > 100
                ? 'text-red-600'
                : metrics.errorCount > 50
                  ? 'text-orange-600'
                  : 'text-gray-900'
            }`}
          >
            {metrics.errorCount}
          </div>
        </div>
      </div>

      {/* Database & Server */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Database Status */}
        <div className="p-4 border border-gray-200 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Database className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">Base de Donnees</h3>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Status</span>
              <span
                className={`px-2 py-1 rounded-lg text-sm flex items-center gap-1 ${DATABASE_STATUS_COLORS[metrics.database.status].bg} ${DATABASE_STATUS_COLORS[metrics.database.status].text}`}
              >
                {getStatusIcon(metrics.database.status)}
                {metrics.database.status === 'healthy'
                  ? 'OK'
                  : metrics.database.status === 'degraded'
                    ? 'Degrade'
                    : 'Hors ligne'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Temps de reponse</span>
              <span className="font-medium text-gray-900">{metrics.database.responseTime}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Connexions actives</span>
              <span className="font-medium text-gray-900">{metrics.database.activeConnections}</span>
            </div>
          </div>
        </div>

        {/* Server Status */}
        <div className="p-4 border border-gray-200 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Server className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">Serveur</h3>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Uptime</span>
              <span className="font-medium text-gray-900">{formatUptime(metrics.server.uptime)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Memoire</span>
              <span
                className={`font-medium ${memoryUsage > 80 ? 'text-red-600' : memoryUsage > 60 ? 'text-orange-600' : 'text-gray-900'}`}
              >
                {formatBytes(metrics.server.memory.heapUsed)} /{' '}
                {formatBytes(metrics.server.memory.heapTotal)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Load Average</span>
              <span className="font-medium text-gray-900">
                {metrics.server.loadAverage.map((l) => l.toFixed(2)).join(' | ')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Node.js</span>
              <span className="font-medium text-gray-900">{metrics.server.nodeVersion}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Memory Progress Bar */}
      <div className="p-4 border border-gray-200 rounded-xl">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-600">Utilisation Memoire</span>
          <span
            className={`font-semibold ${memoryUsage > 80 ? 'text-red-600' : memoryUsage > 60 ? 'text-orange-600' : 'text-teal-600'}`}
          >
            {memoryUsage.toFixed(1)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all ${
              memoryUsage > 80 ? 'bg-red-500' : memoryUsage > 60 ? 'bg-orange-500' : 'bg-teal-500'
            }`}
            style={{ width: `${Math.min(memoryUsage, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
}
